import { MgrStageBadge } from "./org/MgrStageBadge";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarDays, Lock, Unlock, Check, X, Loader2, Plus, Trash2, Clock, History, Plane } from "lucide-react";
import { FlightBookingDialog } from "./FlightBookingDialog";
import { LeaveDetailDialog, leaveState } from "./LeaveDetailDialog";
import { FlightBookingsButton } from "./FlightBookingsList";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { LeaveDueList } from "./LeaveDueList";
import { MuqeemLeaveImport } from "./MuqeemLeaveImport";
import { TravelStatusPanel } from "./TravelStatusPanel";
import { LeaveRangeDialog, PortalBalanceCard } from "./LeaveRangeDialog";
import { api } from "../lib/api";
import { WaSendButton } from "./WaSendButton";
import { useLang } from "../i18n/LanguageContext";
import { personMatch } from "../lib/searchText";

const hijri = (y, m) => {
  try { return new Intl.DateTimeFormat("ar-SA-u-ca-islamic-umalqura", { month: "long", year: "numeric" }).format(new Date(y, m - 1, 15)); }
  catch { return ""; }
};
const thisYear = new Date().getFullYear();

export const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;

const PastLeaveDialog = ({ onClose, onSaved, init }) => {
  const { t, dir } = useLang();
  const AR_MONTHS = t("leave.months");
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState("");
  const [q, setQ] = useState("");
  const [year, setYear] = useState(init?.year || thisYear - 1);
  const [month, setMonth] = useState(init?.month || 1);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState({ start_date: "", end_date: "" });
  useEffect(() => { api.listEmployees().then((d) => setEmps(Array.isArray(d) ? d : (d.employees || d.items || []))).catch(() => {}); }, []);
  const qq = q.trim().toLowerCase();
  const filtered = emps.filter((e) => personMatch(e, qq)).slice(0, 60);
  const save = async () => {
    if (!empId) { toast.error(t("leave.past.selectEmp")); return; }
    setBusy(true);
    try { await api.addManualLeave(empId, year, month, range.start_date && range.end_date ? range : {}); toast.success(t("leave.past.saved")); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("leave.past.saveFail")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="past-leave-dialog">
        <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}><DialogTitle className="font-heading text-lg">{init ? "تسجيل إجازة يدوي (معتمدة)" : t("leave.past.title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="ابحث بالاسم أو رقم الإقامة…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 text-sm" data-testid="past-leave-search" />
          <div data-testid="past-leave-employee" className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-slate-400">لا يوجد موظفون مطابقون</p>
            ) : filtered.map((e) => (
              <button type="button" key={e.id} onClick={() => setEmpId(e.id)}
                data-testid={`past-leave-emp-${e.id}`}
                className={`w-full text-right px-3 py-2 text-sm transition flex items-center justify-between gap-2 ${String(empId) === String(e.id) ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-slate-50 text-slate-700"}`}>
                <span className="min-w-0 truncate">{e.name}{e.branch ? ` — ${e.branch}` : ""}{e.iqama_number && <span className="ms-2 font-mono-nums text-[11px] text-slate-400" dir="ltr">{e.iqama_number}</span>}</span>
                {String(empId) === String(e.id) && <Check className="h-4 w-4 shrink-0 text-blue-600" />}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">{t("leave.past.year")}</span>
              <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-8 text-sm font-mono-nums" dir="ltr" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">{t("leave.past.month")}</span>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                {AR_MONTHS.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
              </select></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">من تاريخ (اختياري)</span>
              <Input type="date" value={range.start_date} onChange={(e) => setRange({ ...range, start_date: e.target.value })} className="h-8 text-sm" data-testid="past-leave-start" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">إلى تاريخ</span>
              <Input type="date" value={range.end_date} min={range.start_date} onChange={(e) => setRange({ ...range, end_date: e.target.value })} className="h-8 text-sm" data-testid="past-leave-end" /></label>
          </div>
          {range.start_date && range.end_date && <p className="text-xs font-semibold text-blue-700" data-testid="past-leave-days">{daysBetween(range.start_date, range.end_date)} يوم — تُخصم من رصيد الموظف</p>}
          <p className="text-[11px] text-slate-400">{t("leave.past.note")}</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("leave.cancel")}</Button>
          <Button onClick={save} disabled={busy} data-testid="past-leave-save" className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("leave.past.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LeaveBoard = ({ mode = "manager", canEdit = true }) => {
  const { t } = useLang();
  const AR_MONTHS = t("leave.months");
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);
  const [manualAt, setManualAt] = useState(null);
  const [dueKey, setDueKey] = useState(0);
  const [lq, setLq] = useState("");
  const [lst, setLst] = useState("");
  const [lbr, setLbr] = useState("");
  const [pick, setPick] = useState(null);
  const [bal, setBal] = useState(null);
  const [flight, setFlight] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try { setData(mode === "manager" ? await api.listLeaves(year) : await api.portalLeaves(year)); }
    catch { toast.error(t("leave.loadFail")); }
  }, [year, mode, t]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (mode === "portal") api.portalLeaveBalance().then(setBal).catch(() => {}); }, [mode, data]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || t("leave.actionFail")); return false; }
    finally { setBusy(false); }
  };

  const toggleLock = async (ym) => {
    const cur = data?.locked_months || [];
    const next = cur.includes(ym) ? cur.filter((x) => x !== ym) : [...cur, ym];
    await act(() => api.updateSettings({ leave_locked_months: next }), cur.includes(ym) ? t("leave.monthOpened") : t("leave.monthLocked"));
  };

  const yearBar = (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-blue-600" /> {t("leave.title")}</h3>
      <div className="flex flex-wrap items-center gap-1.5">
        {mode === "manager" && canEdit && (
          <Button size="sm" data-testid="past-leave-btn" variant="outline" onClick={() => setPastOpen(true)} className="gap-1.5 h-8 border-blue-200 text-blue-600 hover:bg-blue-50">
            <History className="h-4 w-4" /> {t("leave.pastBtn")}
          </Button>
        )}
        {mode === "manager" && canEdit && (
          <Button size="sm" data-testid="manual-leave-btn" onClick={() => setManualAt({ year, month: new Date().getMonth() + 1 })} className="gap-1.5 h-8 bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4" /> تسجيل يدوي
          </Button>
        )}
        {mode === "manager" && canEdit && <FlightBookingsButton />}
        {mode === "manager" && canEdit && <MuqeemLeaveImport onDone={() => { load(); setDueKey((k) => k + 1); }} />}
        {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
          <button key={y} data-testid={`leave-year-${y}`} onClick={() => setYear(y)}
            className={`rounded-full px-3 py-1 text-sm font-semibold border font-mono-nums ${year === y ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-200"}`}>{y}</button>
        ))}
      </div>
    </div>
  );

  if (!data) return <div className="space-y-4">{yearBar}<div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div></div>;

  const maxPer = data.max_per_month || 4;

  // Manager: group leaves by ym
  const byYm = {};
  const allLeaves = data.leaves || [];
  const lBranches = [...new Set(allLeaves.map((l) => l.branch).filter(Boolean))].sort();
  const lqq = lq.trim().toLowerCase();
  const fLeaves = allLeaves.filter((l) => (!lst || l.status === lst) && (!lbr || l.branch === lbr) && (!lqq || personMatch(l, lqq)));
  if (mode === "manager") fLeaves.forEach((l) => { (byYm[l.ym] = byYm[l.ym] || []).push(l); });
  const cntA = allLeaves.filter((l) => l.status === "approved").length, cntP = allLeaves.filter((l) => l.status === "pending").length;
  const box = (k, label, n, cls) => (
    <button type="button" data-testid={`leave-kpi-${k || "all"}`} onClick={() => setLst(k)}
      className={`rounded-2xl border bg-white p-3 text-start transition hover:shadow-md ${lst === k ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200"}`}>
      <p className="text-xs text-slate-500">{label}</p><p className={`font-heading text-xl font-bold tabular-nums ${cls}`}>{n}</p>
    </button>
  );
  const locked = data.locked_months || [];

  return (
    <div className="space-y-4" data-testid={mode === "manager" ? "leave-board-manager" : "leave-board-portal"}>
      {yearBar}
      {flight && <FlightBookingDialog open onOpenChange={(v) => !v && setFlight(null)} leave={flight} />}
      {detail && <LeaveDetailDialog leave={detail} canEdit={canEdit} onClose={() => setDetail(null)}
        onChanged={() => { setDetail(null); load(); setDueKey((k) => k + 1); }}
        onFlight={() => { setFlight(detail); setDetail(null); }}
        onDelete={() => { const id = detail.id; setDetail(null); act(() => api.deleteLeave(id), t("leave.deleted")); }} />}
      {mode === "portal" && <PortalBalanceCard b={bal} />}
      {mode === "manager" && <TravelStatusPanel refreshKey={dueKey} />}
      {mode === "manager" && <LeaveDueList key={dueKey} />}
      {mode === "manager" && (
        <div className="space-y-2" data-testid="leave-filters">
          <div className="grid grid-cols-3 gap-2 max-w-xl">
            {box("", `إجازات ${year}`, allLeaves.length, "text-slate-800")}
            {box("approved", "معتمدة", cntA, "text-emerald-600")}
            {box("pending", "بانتظار الموافقة", cntP, "text-amber-600")}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid="leave-search" value={lq} onChange={(e) => setLq(e.target.value)} placeholder="بحث بالاسم أو الإقامة أو المعرف" className="h-9 w-56 text-sm" />
            <select data-testid="leave-branch-filter" value={lbr} onChange={(e) => setLbr(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
              <option value="">كل الفروع</option>{lBranches.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            {(lq || lst || lbr) && <button type="button" data-testid="leave-filter-clear" onClick={() => { setLq(""); setLst(""); setLbr(""); }} className="text-xs font-semibold text-blue-600 hover:underline">مسح الفلاتر ({fLeaves.length})</button>}
          </div>
        </div>
      )}
      <p className="text-xs text-slate-400">{t("leave.maxHint", { max: maxPer })}{mode === "portal" ? ` · ${t("leave.yourBranch", { b: data.branch || "—" })}` : ""}{t("leave.hijriNote")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {AR_MONTHS.map((name, i) => {
          const m = i + 1;
          const ym = `${year}-${String(m).padStart(2, "0")}`;
          if (mode === "manager") {
            const rows = byYm[ym] || [];
            const approved = rows.filter((r) => r.status === "approved");
            const pending = rows.filter((r) => r.status === "pending");
            const isLocked = locked.includes(ym);
            return (
              <div key={m} data-testid={`leave-month-${ym}`} className={`rounded-2xl border p-3 ${isLocked ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-slate-800 text-sm">{name} <span className="font-mono-nums text-slate-400">{year}</span></p>
                    <p className="text-[10px] text-slate-400">{hijri(year, m)}</p>
                  </div>
                  {canEdit && (
                    <span className="flex items-center gap-1">
                    <button data-testid={`leave-add-${ym}`} onClick={() => setManualAt({ year, month: m })} title="تسجيل إجازة يدوي في هذا الشهر"
                      className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50"><Plus className="h-4 w-4" /></button>
                    <button data-testid={`leave-lock-${ym}`} onClick={() => toggleLock(ym)} disabled={busy}
                      className={`p-1.5 rounded-lg ${isLocked ? "text-rose-600 bg-rose-100" : "text-slate-400 hover:bg-slate-100"}`} title={isLocked ? t("leave.locked") : t("leave.unlocked")}>
                      {isLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                    </button>
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  {approved.map((r) => (
                    <span key={r.id} data-testid={`leave-approved-${r.id}`} className={`inline-flex items-center gap-1 text-[11px] rounded-2xl border px-2 py-0.5 ${leaveState(r).k === "late" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                      <button type="button" data-testid={`leave-open-${r.id}`} onClick={() => setDetail(r)} className="text-start hover:underline leading-tight">
                        <span className="block">{r.employee_name}{r.days ? <b className="font-mono-nums"> · {r.days}ي</b> : null}</span>
                        {r.start_date && <span className="block text-[10px] font-mono-nums opacity-80" dir="ltr">{r.start_date.slice(5).replace("-", "/")} → {r.end_date?.slice(5).replace("-", "/")}</span>}
                        <span className={`block text-[10px] ${leaveState(r).cls}`} data-testid={`leave-state-${r.id}`}>{leaveState(r).label}</span>
                      </button>
                      {canEdit && <button data-testid={`leave-flight-${r.id}`} title="حجز تذكرة طيران" onClick={() => setFlight(r)} className="text-sky-500 hover:text-sky-700"><Plane className="h-3 w-3" /></button>}
                      {canEdit && <button onClick={() => act(() => api.deleteLeave(r.id), t("leave.deleted"))} className="text-emerald-400 hover:text-rose-600"><X className="h-3 w-3" /></button>}
                    </span>
                  ))}
                  <span className="text-[10px] text-slate-400 font-mono-nums">{approved.length}/{maxPer}</span>
                </div>
                {pending.length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                    {pending.map((r) => (
                      <div key={r.id} data-testid={`leave-pending-${r.id}`} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 flex items-center gap-1"><Clock className="h-3 w-3 text-amber-500" />{r.employee_name}{r.days ? <span className="font-mono-nums text-[10px] text-slate-400" dir="ltr">{r.start_date?.slice(5)}→{r.end_date?.slice(5)} ({r.days}{r.unpaid_days ? `، ${r.unpaid_days} بدون راتب` : ""})</span> : null}<MgrStageBadge r={r} testid={`leave-mgr-stage-${r.id}`} /></span>
                        {canEdit && (
                          <span className="flex items-center gap-1">
                            <button data-testid={`leave-approve-${r.id}`} onClick={async () => { if (await act(() => api.approveLeave(r.id), t("leave.approved"))) setFlight(r); }} className="p-1 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><Check className="h-3.5 w-3.5" /></button>
                            <WaSendButton employeeId={r.employee_id} employeeName={r.employee_name} refType="leave" refId={r.id} testid={`leave-wa-${r.id}`} className="!h-6 !w-6"
                              text={`السلام عليكم ${r.employee_name || ""}\nبخصوص طلب الإجازة من ${r.start_date || ""} إلى ${r.end_date || ""}: `} />
                            <button data-testid={`leave-reject-${r.id}`} onClick={() => act(() => api.rejectLeave(r.id), t("leave.rejected"))} className="p-1 rounded bg-rose-50 text-rose-600 hover:bg-rose-100"><X className="h-3.5 w-3.5" /></button>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          // portal mode
          const mo = (data.months || []).find((x) => x.ym === ym) || {};
          const ineligible = mo.eligible === false && !mo.my_status;
          const disabled = busy || mo.locked || ineligible || (mo.full && !mo.my_status);
          const onClick = () => {
            if (mo.my_status) { act(() => api.cancelLeave(mo.my_leave_id), t("leave.cancelled")); return; }
            if (mo.locked) { toast.warning(t("leave.monthClosed")); return; }
            if (ineligible) { toast.warning(t("leave.cannotBefore", { d: data.next_eligible, g: data.gap_months || 14 })); return; }
            if (mo.full) { toast.warning(t("leave.monthFull")); return; }
            setPick({ year, month: m, name });
          };
          return (
            <button key={m} data-testid={`leave-month-${ym}`} onClick={onClick} disabled={disabled && !mo.my_status}
              className={`text-right rounded-2xl border p-3 transition ${mo.my_status ? "border-blue-400 ring-1 ring-blue-300 bg-blue-50" : mo.locked ? "border-rose-200 bg-rose-50/40" : ineligible ? "border-slate-200 bg-slate-50 opacity-70" : mo.full ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-bold text-slate-800 text-sm">{name} <span className="font-mono-nums text-slate-400">{year}</span></p>
                  <p className="text-[10px] text-slate-400">{hijri(year, m)}</p>
                </div>
                {mo.locked ? <Lock className="h-4 w-4 text-rose-500" /> : mo.my_status ? (mo.my_status === "approved" ? <Check className="h-4 w-4 text-emerald-600" /> : <Clock className="h-4 w-4 text-amber-500" />) : ineligible ? <Lock className="h-4 w-4 text-slate-300" /> : <Plus className="h-4 w-4 text-blue-400" />}
              </div>
              <div className="mt-2 text-[11px]">
                {mo.locked ? <span className="text-rose-600">{t("leave.cell.closed")}</span>
                  : mo.my_status === "approved" ? <span className="text-emerald-700 flex items-center gap-1">{t("leave.cell.approvedTap")} <Trash2 className="h-3 w-3" /></span>
                  : mo.my_status === "pending" ? <span className="text-amber-600 flex items-center gap-1">{t("leave.cell.pendingTap")} <Trash2 className="h-3 w-3" /></span>
                  : null}
                {mo.my_days ? <span className="mt-0.5 block font-mono-nums text-slate-500" dir="ltr" data-testid={`leave-my-range-${ym}`}>{mo.my_start} → {mo.my_end} ({mo.my_days})</span> : null}
                {mo.locked || mo.my_status ? null
                  : ineligible ? <span className="text-slate-400">{t("leave.cell.notAvailable", { d: data.next_eligible })}</span>
                  : mo.full ? <span className="text-slate-400">{t("leave.cell.full", { a: mo.approved_count, m: maxPer })}</span>
                  : <span className="text-blue-600">{t("leave.cell.available", { a: mo.approved_count, m: maxPer })}</span>}
              </div>
            </button>
          );
        })}
      </div>
      {pick && <LeaveRangeDialog pick={pick} balance={bal} onClose={() => setPick(null)}
        onSubmit={(r) => act(() => api.requestLeave(pick.year, pick.month, r), t("leave.requestSent")).then(() => setPick(null))} />}
      {pastOpen && <PastLeaveDialog onClose={() => setPastOpen(false)} onSaved={() => { setPastOpen(false); load(); }} />}
      {manualAt && <PastLeaveDialog init={manualAt} onClose={() => setManualAt(null)} onSaved={() => { setManualAt(null); load(); }} />}
    </div>
  );
};
